import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import MaterialTable from 'material-table';
import { Paper, Typography, Fab, IconButton, Tooltip } from '@material-ui/core';
import { Delete, Refresh, Add } from '@material-ui/icons';

import * as actions from './store/warehouseActions';
import * as action from '../../np-core/Auth/store/authActions';
import { BackButton, localization } from '../../sc-utilities';
import { hasPermission, createHistory } from '../../sc-utilities';
import WarehouseDelete from './WarehouseDelete';
import Toolbar from '../../sc-utilities/Toolbar'
import { CsvBuilder } from 'filefy';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        }
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];
let loading = true

const WarehouseList = props => {
    const { classes, translations, permissions, onFetchWarehouseList, fetching, saveColumnsFilter, deleting, activeCustomerID, onBulkDeleteWarehouses, tableColumns, tableRef } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, warehouseIDs: [] });
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, warehouseID: null, redirectPath: '' });
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);
    const customExport = (columns, warehouseComponentList) => {
        if (columns && columns[0].field === 'Action') {
            columns.shift()
        }
        const data = warehouseComponentList.map(warehouseComponent => {
            let returnObj = [
                warehouseComponent.WarehouseInventoryID ? warehouseComponent.WarehouseInventoryID : translations['short_not_assigned'] || 'n/a',
                warehouseComponent.Name ? warehouseComponent.Name : translations['short_not_assigned'] || 'n/a',
                warehouseComponent.ContactPerson ? warehouseComponent.ContactPerson : translations['short_not_assigned'] || 'n/a',
                warehouseComponent.Deleted ? 'Yes' : 'No'
            ];
            return returnObj;
        });
        const builder = new CsvBuilder(`securaze_warehouse_${Math.floor(Date.now() / 1000)}.csv`);
        builder
            .setDelimeter('|')
            .setColumns(columns.map(columnDef => columnDef.title))
            .addRows(data)
            .exportFile();
    };

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: true,
        selectionProps: rowData => {
            return { id: `checkbox_${rowData.WarehouseID}` }
        },
        search: false,
        minBodyHeight: window.innerHeight - 198 < 300 ? 300 : window.innerHeight - 198,
        maxBodyHeight: window.innerHeight - 198,
        pageSizeOptions: rowsPageSizeInit,
        exportCsv: customExport,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? '#f3f3f3' : '#ffffff' };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);
        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'] });


        let result = await onFetchWarehouseList(query);
        let rowsPearPage = [20, 50, 100]
        if (result.count < 20 && result.count !== 0) {
            rowsPearPage.unshift(result.count)
        }
        if (result.count === 0) {
            rowsPearPage.unshift(5)
        }
        if (result.count > 20 && result.count < 50) {
            rowsPearPage = [20, result.count, 50, 100]
        }
        if (loading) {
            pageSize = result.count !== 0 ? result.count < 20 ? result.count : result.count < 50 ? result.count : 50 : 5
            rowsPageSizeInit = rowsPearPage;
            loading = false;
            query['pageSize'] = result.count !== 0 ? result.count < 20 ? result.count : result.count < 50 ? result.count : 50 : 5
        };
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result.data, page: query.page, pageSize: pageSize, totalCount: result.count };
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography >{translations['warehouses'] || 'Warehouses'}</Typography>
                {
                    hasPermission(permissions, 'warehouse_create') ?
                        <Fab
                            id='create_warehouse'
                            variant="extended"
                            size="small"
                            style={{ borderRadius: '1px' }}
                            color="primary"
                            aria-label="Add"
                            className={classes.fab}
                            onClick={() => redirectHandler(`/warehouse/create`)}
                        >
                            <Add size="small" />
                            {translations["create_new"] || "Create new"}
                        </Fab> : null
                }
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh id={'refresh_button'} />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    }

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, warehouseIDs: [], forBulk: false });
    }

    const handleCloseDanger = async () => {
        await onBulkDeleteWarehouses({ WarehouseIDs: deleteModalState.warehouseIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, warehouseIDs: [], forBulk: false });
        tableRef.current && tableRef.current.onQueryChange()
    };

    const handleBulkDelete = warehouseIDs => {
        setDeleteModalState({ ...deleteModalState, open: true, warehouseIDs: warehouseIDs, forBulk: true });
    };

    const redirectHandler = (path, warehouseID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, warehouseID: warehouseID, redirectPath: path })
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    // Table Settings
    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["warehouses"] || "Warehouses"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(warehouses => warehouses.WarehouseID))
        }
    ]

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`Warehouse`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    }

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                actions={tableActions}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {
                hasPermission(permissions, 'warehouse_delete') ? (
                    <WarehouseDelete
                        activeCustomerID={activeCustomerID}
                        entityName={translations['warehouses'] || 'Warehouses'}
                        translations={translations}
                        handleCloseDanger={handleCloseDanger}
                        handleCloseSafe={handleCloseSafe}
                        deleteModalState={deleteModalState}
                        deleting={deleting}
                    />
                ) : null
            }
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
        warehouse: state.warehouses.warehouse,
        fetching: state.warehouses.warehouse.fetching,
        deleting: state.warehouses.warehouse.deleting
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchWarehouseList: filter => dispatch(actions.fetchWarehouse(filter)),
        onBulkDeleteWarehouses: data => dispatch(actions.bulkDeleteWarehouses(data)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(WarehouseList));



/**
 *  Translations:
 *      warehouse
 *      warehouses
 *      edit_warehouse
 *
 */